import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, Check } from 'lucide-react';
import { RetirarCopiasStep1 } from './RetirarCopiasStep1';
import { RetirarCopiasStep2Single } from './RetirarCopiasStep2Single';
import { RetirarCopiasStep2Multiple } from './RetirarCopiasStep2Multiple';
import retiroCopiasService from '../../services/retiroCopiasService';
import type {
  OtroTramiteRecord,
  DistribucionMode,
  RetiradorData,
  RetiradorErrors,
  RetiradorPayload,
} from '../../types';

interface RetirarCopiasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tramite: OtroTramiteRecord;
}

const createEmptyRetirador = (): RetiradorData => ({
  nombreCompleto: '',
  cantidadCopias: 1,
  firmaDigital: null,
});

export const RetirarCopiasDialog: React.FC<RetirarCopiasDialogProps> = ({
  open,
  onOpenChange,
  tramite,
}) => {
  const queryClient = useQueryClient();

  // State
  const [step, setStep] = useState<1 | 2>(1);
  const [cantidadCopias, setCantidadCopias] = useState(0);
  const [distribucionMode, setDistribucionMode] = useState<DistribucionMode>('single');
  const [fechaRetiro, setFechaRetiro] = useState(new Date().toISOString().split('T')[0]);
  const [retiradores, setRetiradores] = useState<RetiradorData[]>([createEmptyRetirador()]);
  const [errors, setErrors] = useState<RetiradorErrors[]>([]);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const copiasDisponibles = tramite.copiasTotales - tramite.copiasActuales;
  const shouldSkipStep1 = copiasDisponibles === 1;

  // Mutation
  const registrarRetiroMutation = useMutation({
    mutationFn: async () => {
      const retiradorPayloads: RetiradorPayload[] = retiradores.map((r) => ({
        nombreCompleto: r.nombreCompleto,
        cantidadCopias: r.cantidadCopias,
        firmaDigital: r.firmaDigital!,
      }));

      return retiroCopiasService.registrarRetiro(
        tramite.id,
        fechaRetiro,
        retiradorPayloads
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retiros', tramite.id] });
      queryClient.invalidateQueries({ queryKey: ['otros-tramites'] });
      onOpenChange(false);
      // TODO: Mostrar toast de éxito
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al registrar el retiro';
      setGeneralError(message);
    },
  });

  // Auto-initialize when only 1 copy available
  useEffect(() => {
    if (open && shouldSkipStep1) {
      setStep(2);
      setCantidadCopias(1);
      setDistribucionMode('single');
      setRetiradores([{
        ...createEmptyRetirador(),
        cantidadCopias: 1,
      }]);
    }
  }, [open, shouldSkipStep1]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setStep(shouldSkipStep1 ? 2 : 1);
      setCantidadCopias(shouldSkipStep1 ? 1 : 0);
      setDistribucionMode('single');
      setFechaRetiro(new Date().toISOString().split('T')[0]);
      setRetiradores([createEmptyRetirador()]);
      setErrors([]);
      setGeneralError(null);
    }
  }, [open, shouldSkipStep1]);

  // Update retiradores when mode or cantidad changes
  useEffect(() => {
    if (distribucionMode === 'single' && cantidadCopias > 0) {
      setRetiradores([{
        ...createEmptyRetirador(),
        cantidadCopias,
      }]);
    } else if (distribucionMode === 'multiple' && retiradores.length === 1 && retiradores[0].cantidadCopias !== 1) {
      // Reset to single retirador when switching to multiple
      setRetiradores([createEmptyRetirador()]);
    }
  }, [distribucionMode, cantidadCopias]);

  // Validations
  const validateStep1 = (): boolean => {
    if (cantidadCopias < 1 || cantidadCopias > copiasDisponibles) {
      setGeneralError(`Debe seleccionar entre 1 y ${copiasDisponibles} copias`);
      return false;
    }
    setGeneralError(null);
    return true;
  };

  const validateRetirador = (retirador: RetiradorData): RetiradorErrors => {
    const errors: RetiradorErrors = {};

    if (!retirador.nombreCompleto || retirador.nombreCompleto.trim().length < 3) {
      errors.nombreCompleto = 'El nombre completo debe tener al menos 3 caracteres';
    }

    if (!retirador.cantidadCopias || retirador.cantidadCopias < 1) {
      errors.cantidadCopias = 'Debe seleccionar al menos 1 copia';
    }

    if (!retirador.firmaDigital) {
      errors.firmaDigital = 'La firma digital es obligatoria';
    }

    return errors;
  };

  const validateStep2 = (): boolean => {
    const newErrors: RetiradorErrors[] = retiradores.map(validateRetirador);
    setErrors(newErrors);

    const hasErrors = newErrors.some(e => Object.keys(e).length > 0);
    if (hasErrors) {
      setGeneralError('Por favor complete todos los campos obligatorios');
      return false;
    }

    // Validate total copies
    const totalCopias = retiradores.reduce((sum, r) => sum + r.cantidadCopias, 0);
    if (totalCopias !== cantidadCopias) {
      setGeneralError(`El total de copias asignadas (${totalCopias}) no coincide con las seleccionadas (${cantidadCopias})`);
      return false;
    }

    setGeneralError(null);
    return true;
  };

  // Handlers
  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (shouldSkipStep1) {
      onOpenChange(false);
    } else {
      setStep(1);
      setGeneralError(null);
    }
  };

  const handleSubmit = async () => {
    if (validateStep2()) {
      await registrarRetiroMutation.mutateAsync();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="break-words">
            Retirar Copias - {tramite.id}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && !shouldSkipStep1 && 'Seleccione la cantidad de copias y cómo desea distribuirlas'}
            {step === 2 && shouldSkipStep1 && 'Complete los datos para retirar la copia'}
            {step === 2 && !shouldSkipStep1 && 'Complete los datos de quién retira las copias'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 overflow-x-hidden">
          {/* Step indicator - only show if there are multiple steps */}
          {!shouldSkipStep1 && (
            <div className="flex items-center gap-4 mb-6">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${step >= 1 ? 'border-primary bg-primary text-white' : 'border-gray-300'}`}>
                  {step > 1 ? <Check className="h-4 w-4" /> : '1'}
                </div>
                <span className="text-sm font-medium">Cantidad</span>
              </div>

              <div className={`flex-1 h-0.5 ${step >= 2 ? 'bg-primary' : 'bg-gray-300'}`} />

              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${step >= 2 ? 'border-primary bg-primary text-white' : 'border-gray-300'}`}>
                  2
                </div>
                <span className="text-sm font-medium">Datos</span>
              </div>
            </div>
          )}

          {/* Step content */}
          {step === 1 && !shouldSkipStep1 && (
            <RetirarCopiasStep1
              copiasDisponibles={copiasDisponibles}
              cantidadSeleccionada={cantidadCopias}
              onCantidadChange={setCantidadCopias}
              distribucionMode={distribucionMode}
              onModeChange={setDistribucionMode}
              error={generalError || undefined}
            />
          )}

          {step === 2 && distribucionMode === 'single' && (
            <RetirarCopiasStep2Single
              cantidadCopias={cantidadCopias}
              fechaRetiro={fechaRetiro}
              onFechaChange={setFechaRetiro}
              retirador={retiradores[0]}
              onRetiradorChange={(r) => setRetiradores([r])}
              errors={errors[0]}
              disabled={registrarRetiroMutation.isPending}
            />
          )}

          {step === 2 && distribucionMode === 'multiple' && (
            <RetirarCopiasStep2Multiple
              cantidadCopias={cantidadCopias}
              fechaRetiro={fechaRetiro}
              onFechaChange={setFechaRetiro}
              retiradores={retiradores}
              onRetiradoresChange={setRetiradores}
              errorsArray={errors}
              disabled={registrarRetiroMutation.isPending}
            />
          )}
        </div>

        {/* General error */}
        {generalError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{generalError}</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 1 && !shouldSkipStep1 && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleNext}
                disabled={cantidadCopias < 1}
              >
                Siguiente
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={registrarRetiroMutation.isPending}
              >
                {shouldSkipStep1 ? 'Cancelar' : 'Atrás'}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                loading={registrarRetiroMutation.isPending}
                disabled={registrarRetiroMutation.isPending}
              >
                Registrar Retiro
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
